"use client";

import React, { useEffect, useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { SearchIcon, X } from "lucide-react";
import { SearchHotelResult } from "./SearchHotelResult";
import fuzzysort from "fuzzysort";
import useJapanAreasStore from "@/store/hooks/useGlobal";
import { Link } from "@/components/atoms/link";
import { iconList } from "@/assets/icons";
import useSearchFormStore from "@/store/searchFormStore/searchFormStore";
import {
  HeaderTextSettingQuery,
  JapanHotel,
  UploadFile,
} from "@/types/generated";
import { getAreaHotelThumbnailUrl } from "@app/[locale]/tourist-attraction/[city_slug]/[slug]/util";
import { DEFAULT_LOCALE } from "@/i18n/routing";
import { useIsMobile } from "@/hooks/useIsMobile/useIsMobile";
import { MOBILE_BREAKPOINT } from "@/constants";

export enum DISPLAY_TYPE {
  DESTINATION,
  HOTEL,
}
interface ProcessedHotelDetails extends JapanHotel {
  area_name?: string;
  area_item_name?: string;
}

interface ISearchBarProps {
  locale: string;
  pathName?: string;
  headerTextSettings?: HeaderTextSettingQuery;
}
export function SearchBar(props: Readonly<ISearchBarProps>) {
  const { locale, pathName, headerTextSettings } = props;
  const isMobile = useIsMobile(MOBILE_BREAKPOINT);

  const [open, setOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [displayType, setDisplayType] = useState(DISPLAY_TYPE.DESTINATION);
  const [searchValue, setSearchValue] = useState<string | undefined>();
  const {
    japanAreas,
    loading: loadingJapanAreas,
    allLocaleHotels,
    loadingAllLocaleHotels,
  } = useJapanAreasStore();
  const { fetchConfigurationsData, configurations } = useSearchFormStore();

  const { Spinner } = iconList;

  const searchBoxData = headerTextSettings?.headerTextSettings[0]?.search_box;

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setOpen(false);
      setIsClosing(false);
      setDisplayType(DISPLAY_TYPE.DESTINATION);
      setSearchValue(undefined);
    }, 500);
  };

  const mappedDestinationData = useMemo(() => {
    return (japanAreas || [])
      .map((i) => ({
        key: i.documentId,
        id: i.documentId,
        name: i.area_name,
        slug: i.slug_hotel || i.item_name || "",
      }))
      .filter((i) => !!i.name);
  }, [japanAreas]);

  type HotelInFinalList = {
    id: string;
    name: string;
    searchNames: string[];
    img: UploadFile | undefined;
    area_name: string;
    externalUrl: string;
    item_name: string;
    area_item_name: string;
    slug: string;
    citySearchNames: string[];
  };
  interface HotelWithScore extends HotelInFinalList {
    _fuzzysortScore?: number;
    _contributingScores?: { name: string; score: number }[];
  }

  const baseHotelList = useMemo(() => {
    if (!allLocaleHotels || !japanAreas?.length) {
      return [];
    }

    // Build a map of current-locale hotels annotated with area info
    const buildCurrentLocaleHotelsMap = (): Map<
      string,
      ProcessedHotelDetails
    > => {
      const map = new Map<string, ProcessedHotelDetails>();
      for (const area of japanAreas) {
        const areaName = area.area_name ?? undefined;
        const areaItemName = area.item_name ?? undefined;

        for (const hotel of area.hotels as JapanHotel[]) {
          if (hotel && typeof hotel === "object" && hotel.documentId) {
            map.set(hotel.documentId, {
              ...hotel,
              area_name: areaName,
              area_item_name: areaItemName,
            });
          }
        }
      }
      return map;
    };

    // Aggregate hotel data from all locales into a map keyed by documentId
    const aggregateHotelData = (
      currentLocaleMap: Map<string, ProcessedHotelDetails>,
    ) => {
      type AggregatedEntry = {
        displayDetails: ProcessedHotelDetails | undefined;
        searchNamesSet: Set<string>;
      };

      const aggregator = new Map<string, AggregatedEntry>();

      const isValidHotel = (
        hotel: unknown,
      ): hotel is Record<string, unknown> & { documentId: string } =>
        !!hotel &&
        typeof hotel === "object" &&
        "documentId" in (hotel as Record<string, unknown>);

      const ensureAggregatorEntry = (docId: string) => {
        if (!aggregator.has(docId)) {
          const displayDetails = currentLocaleMap.get(docId);
          aggregator.set(docId, {
            displayDetails,
            searchNamesSet: new Set<string>(),
          });
        }
      };

      const addHotelNames = (
        aggregatedHotel: AggregatedEntry,
        hotel: Record<string, unknown>,
      ) => {
        const properties = hotel.properties as
          | Record<string, unknown>
          | undefined;
        const hotelNameFromProps = properties
          ? (properties.hotel_name as string | undefined)
          : undefined;
        const itemNameFromHotel = hotel.item_name as string | undefined;
        const displayNameFromHotel = hotel.display_name as string | undefined;

        for (const name of [
          hotelNameFromProps,
          itemNameFromHotel,
          displayNameFromHotel,
        ]) {
          if (name) aggregatedHotel.searchNamesSet.add(name);
        }
      };

      for (const [, hotelsInThisLocale] of Object.entries(allLocaleHotels)) {
        if (!hotelsInThisLocale) continue;

        for (const hotel of hotelsInThisLocale) {
          if (!isValidHotel(hotel)) continue;

          ensureAggregatorEntry(hotel.documentId);

          const aggregatedHotel = aggregator.get(hotel.documentId)!;
          addHotelNames(aggregatedHotel, hotel);
        }
      }

      return aggregator;
    };

    // Build the final hotel list from aggregated data
    const buildFinalHotelList = (
      hotelDataAggregator: Map<
        string,
        {
          displayDetails: ProcessedHotelDetails | undefined;
          searchNamesSet: Set<string>;
        }
      >,
    ): HotelInFinalList[] => {
      const resultList: HotelInFinalList[] = [];

      for (const [docId, data] of Array.from(hotelDataAggregator.entries())) {
        const details = data.displayDetails;

        const hotelName =
          details?.properties?.hotel_name ||
          details?.item_name ||
          Array.from(data.searchNamesSet)[0] ||
          "Unnamed Hotel";

        const citySearchNames = [
          details?.area_name,
          details?.area_item_name,
        ].filter(Boolean) as string[];

        resultList.push({
          id: docId,
          name: hotelName,
          searchNames: Array.from(data.searchNamesSet),
          img: getAreaHotelThumbnailUrl(details) as UploadFile,
          area_name: details?.area_name || "",
          externalUrl: details?.redirect?.external_url || "",
          item_name: details?.item_name || "",
          area_item_name: details?.area_item_name || "",
          slug: details?.slug || "",
          citySearchNames,
        });
      }

      return resultList;
    };

    const currentLocaleMap = buildCurrentLocaleHotelsMap();
    const hotelDataAggregator = aggregateHotelData(currentLocaleMap);
    return buildFinalHotelList(hotelDataAggregator);
  }, [allLocaleHotels, japanAreas]);

  const mappedHotelData = useMemo(() => {
    const trimmedSearchValue = searchValue?.trim().toLowerCase();

    if (!trimmedSearchValue) {
      return baseHotelList;
    }

    const scoreThreshold = 0.3;
    const matchedHotels = new Map<string, HotelWithScore>();

    const calculateBestScore = (
      searchValue: string,
      target: string,
      contributingScores: { name: string; score: number }[],
      label: string,
    ) => {
      const result = fuzzysort.single(searchValue, target.toLowerCase());
      if (result && result.score > scoreThreshold) {
        contributingScores.push({ name: label, score: result.score });
        return result.score;
      }
      return -Infinity;
    };

    // Evaluate a single hotel and return score, matched flag and contributing scores
    const evaluateHotel = (hotel: HotelInFinalList) => {
      let bestScoreForThisHotel = -Infinity;
      let hotelMatchedOverall = false;
      const contributingScoresInfo: { name: string; score: number }[] = [];

      // Check display name (preserve original order)
      const displayScore = calculateBestScore(
        trimmedSearchValue,
        hotel.name,
        contributingScoresInfo,
        `(display) ${hotel.name}`,
      );
      if (displayScore > -Infinity) {
        bestScoreForThisHotel = Math.max(bestScoreForThisHotel, displayScore);
        hotelMatchedOverall = true;
      }

      // Check searchNames (preserve original loop and order)
      for (const nameInSearchArray of hotel.searchNames) {
        const score = calculateBestScore(
          trimmedSearchValue,
          nameInSearchArray,
          contributingScoresInfo,
          nameInSearchArray,
        );
        if (score > -Infinity) {
          bestScoreForThisHotel = Math.max(bestScoreForThisHotel, score);
          hotelMatchedOverall = true;
        }
      }

      // Check citySearchNames (preserve original guard and loop)
      if (hotel.citySearchNames?.length) {
        for (const cityName of hotel.citySearchNames) {
          if (cityName) {
            const score = calculateBestScore(
              trimmedSearchValue,
              cityName,
              contributingScoresInfo,
              `(city) ${cityName}`,
            );
            if (score > -Infinity) {
              bestScoreForThisHotel = Math.max(bestScoreForThisHotel, score);
              hotelMatchedOverall = true;
            }
          }
        }
      }

      return {
        bestScoreForThisHotel,
        hotelMatchedOverall,
        contributingScoresInfo,
      };
    };

    // Add hotel to matchedHotels map preserving original comparison logic
    const addToMatchedHotels = (
      hotel: HotelInFinalList,
      bestScoreForThisHotel: number,
      contributingScoresInfo: { name: string; score: number }[],
    ) => {
      const currentHotelEntry: HotelWithScore = {
        ...hotel,
        _fuzzysortScore: bestScoreForThisHotel,
        _contributingScores: contributingScoresInfo,
      };

      const existingHotelEntry = matchedHotels.get(hotel.id);
      if (
        !existingHotelEntry ||
        bestScoreForThisHotel >
          (existingHotelEntry._fuzzysortScore || -Infinity)
      ) {
        matchedHotels.set(hotel.id, currentHotelEntry);
      }
    };

    for (const hotel of baseHotelList) {
      const {
        bestScoreForThisHotel,
        hotelMatchedOverall,
        contributingScoresInfo,
      } = evaluateHotel(hotel);
      if (hotelMatchedOverall) {
        addToMatchedHotels(
          hotel,
          bestScoreForThisHotel,
          contributingScoresInfo,
        );
      }
    }

    const fuzzyResults: HotelWithScore[] = Array.from(
      matchedHotels.values(),
    ).sort(
      (a, b) =>
        (b._fuzzysortScore || -Infinity) - (a._fuzzysortScore || -Infinity),
    );

    return fuzzyResults.map((hotel) => {
      const {
        _fuzzysortScore,
        _contributingScores,
        ...hotelWithoutDebugFields
      } = hotel;
      return hotelWithoutDebugFields as HotelWithScore;
    });
  }, [baseHotelList, searchValue]);

  useEffect(() => {
    if (!configurations) {
      fetchConfigurationsData(DEFAULT_LOCALE);
    }
  }, [configurations, fetchConfigurationsData]);

  const renderSearchContent = () => {
    const isSearching =
      displayType === DISPLAY_TYPE.HOTEL || searchValue !== undefined;

    if (isSearching) {
      if (loadingJapanAreas || loadingAllLocaleHotels) {
        return (
          <Spinner
            className="m-auto mt-5 h-10 w-10 animate-spin text-gray-200 dark:text-gray-600"
            fill={"var(--fill-gray-stronger)"}
          />
        );
      } else {
        return (
          <SearchHotelResult
            data={mappedHotelData}
            locale={locale}
            showDestinationLabel={
              searchBoxData?.show_destination_label || "目的地を見る"
            }
            onChangeDisplayType={(type) => {
              setDisplayType(type);
              setSearchValue(undefined);
            }}
          />
        );
      }
    } else {
      return (
        <>
          <h4 className="py-6 text-2xl font-[var(--font-bold)]">
            {searchBoxData?.top_destination_label || "宿泊エリア"}
          </h4>
          {loadingJapanAreas ? (
            <Spinner
              className="m-auto h-10 w-10 animate-spin text-gray-200 dark:text-gray-600"
              fill={"var(--fill-gray-stronger)"}
            />
          ) : (
            <div className="max-h-[400px] overflow-y-auto sm:max-h-[unset]">
              <div className="columns-2 gap-8 text-sm text-gray-700 sm:columns-3">
                {mappedDestinationData.map((i) => (
                  <Link
                    href={`/${locale}/${i.slug}`}
                    key={i.id}
                    className="mb-4 block text-sm text-gray-700 hover:opacity-80"
                  >
                    {i.name}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </>
      );
    }
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(newOpen) => {
        if (!newOpen) {
          handleClose();
          setOpen(true);
        }
      }}
    >
      <Dialog.Trigger asChild>
        <button
          className="mx-2 flex max-w-[200px] cursor-text items-center gap-1 overflow-hidden rounded-md border border-gray-300 px-3 py-2 md:gap-1 lg:h-[46px]"
          onClick={() => setOpen(true)}
        >
          <SearchIcon className="h-4 w-4 flex-shrink-0 text-gray-500 md:h-5 md:w-5" />
          {pathName?.includes("/partners/ctbc") && isMobile ? null : (
            <span className="truncate overflow-hidden text-xs text-ellipsis whitespace-nowrap text-gray-500 md:text-sm">
              {searchBoxData?.searchbox_placeholder || "ホテルを検索"}
            </span>
          )}
        </button>
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-40 bg-black/40 transition-opacity duration-300"
          onClick={(e) => {
            // Close only when clicking directly on the overlay backdrop
            if (e.target === e.currentTarget) {
              handleClose();
            }
          }}
        >
          <div
            className={`relative top-0 left-0 z-50 h-max w-full overflow-y-auto bg-white shadow-xl ${
              isClosing ? "animate-slideOut" : "animate-slideIn"
            }`}
          >
            <div className="mx-auto max-w-4xl p-6">
              <div className="flex items-center justify-between">
                <div className="flex flex-1 items-center rounded-md bg-gray-100 px-4 py-3">
                  <SearchIcon className="mr-3 h-5 w-5 text-gray-500" />
                  <input
                    autoFocus
                    type="text"
                    placeholder={
                      searchBoxData?.searchbox_placeholder || "ホテルを検索"
                    }
                    className="flex-1 bg-transparent text-base text-gray-700 outline-none"
                    onClick={() => setDisplayType(DISPLAY_TYPE.HOTEL)}
                    value={searchValue || ""}
                    onChange={(e) => setSearchValue(e.target.value)}
                  />
                  <button
                    className="ml-4 text-gray-600 hover:text-black"
                    aria-label="Close"
                    onClick={handleClose}
                  >
                    <X className="h-6 w-6 cursor-pointer" />
                  </button>
                </div>
              </div>
              {renderSearchContent()}
            </div>
          </div>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
